import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../firebase';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { doc, setDoc, increment } from 'firebase/firestore';

// ============================================================
// Nexora / MRAM SMS API configuration
// ============================================================
// IMPORTANT:
// This direct browser integration exposes the API key in the
// built JavaScript bundle. For production, move SMS sending to
// a Firebase Cloud Function/backend and keep the API key there.
//
// MRAM API:
// https://sms.mram.com.bd/smsapi
//
// Required parameters:
// api_key, type, contacts, senderid, msg, label
//
// OTP is a machine-generated notification, so the OTP message
// can remain machine-generated. Use "transactional" label.
// ============================================================



const OTP_EXPIRY_MS = 5 * 60 * 1000;

const OTP_STORAGE_KEY = 'nexora_pending_otp';
const TEMP_USER_KEY = 'nexora_pending_registration';

const createSessionToken = () => {
  if (window.crypto?.randomUUID) {
    return (
      window.crypto.randomUUID().replace(/-/g, '') +
      window.crypto.randomUUID().replace(/-/g, '')
    );
  }

  const bytes = new Uint8Array(48);
  window.crypto?.getRandomValues?.(bytes);

  return Array.from(
    bytes,
    (b) => b.toString(16).padStart(2, '0')
  ).join('');
};

const normalizePhone = (phone) => {
  let digits = String(phone || '').replace(/\D/g, '');

  if (digits.startsWith('880')) {
    digits = digits.substring(3);
  }

  if (digits.startsWith('0')) {
    digits = digits.substring(1);
  }

  return digits.length === 10 ? `+880${digits}` : '';
};


const OtpVerification = () => {
  const navigate = useNavigate();

  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const inputRefs = useRef([]);
  const autoVerifyStartedRef = useRef(false);

  const getPendingRegistration = () => {
    try {
      return JSON.parse(
        localStorage.getItem(TEMP_USER_KEY) || '{}'
      );
    } catch {
      return {};
    }
  };

  const completeVerification = async (otpString) => {
    if (!/^\d{6}$/.test(otpString)) {
      setError('দয়া করে ৬ ডিজিটের সম্পূর্ণ OTP কোডটি লিখুন');
      return;
    }

    if (isLoading) return;

    setError('');
    setIsLoading(true);

    try {
      const pendingOtp = JSON.parse(
        localStorage.getItem(OTP_STORAGE_KEY) || 'null'
      );

      const tempUserData = getPendingRegistration();

      if (!pendingOtp || !tempUserData?.phoneNumber) {
        throw new Error('OTP session not found');
      }

      if (pendingOtp.phoneNumber !== tempUserData.phoneNumber) {
        throw new Error('OTP phone mismatch');
      }

      if (Date.now() > Number(pendingOtp.expiresAt)) {
        localStorage.removeItem(OTP_STORAGE_KEY);
        throw new Error('OTP expired');
      }

      if (String(pendingOtp.code) !== String(otpString)) {
        throw new Error('Invalid OTP');
      }

      const formattedPhone = normalizePhone(
        tempUserData.phoneNumber
      );

      if (!formattedPhone) {
        throw new Error('Invalid phone number');
      }

      // Nexora's token field.
      // NOTE: This is an application session token, not a Firebase Auth ID token.
      const sessionToken = createSessionToken();

      // Account is created ONLY after successful OTP verification.
      await setDoc(doc(db, 'users', formattedPhone), {
        uid: formattedPhone,
        fullName: tempUserData.fullName || 'Nexora User',
        phoneNumber: formattedPhone,
        password: tempUserData.password || '',
        token: sessionToken,
        createdAt:
          tempUserData.createdAt ||
          new Date().toISOString(),
        isOnline: true
      });

      // Count only verified accounts.
      await setDoc(
        doc(db, 'totalUser', 'stats'),
        { count: increment(1) },
        { merge: true }
      );

      // Clear one-time data.
      localStorage.removeItem(TEMP_USER_KEY);
      localStorage.removeItem(OTP_STORAGE_KEY);

      setIsLoading(false);

      navigate('/login');
    } catch (err) {
      console.error('OTP Verification Error:', err);

      setIsLoading(false);

      if (err.message === 'OTP expired') {
        setError(
          'OTP-এর মেয়াদ শেষ হয়ে গেছে। আবার OTP পাঠাতে হবে।'
        );
      } else if (err.message === 'Invalid OTP') {
        setError(
          'ভুল OTP কোড দিয়েছেন! আবার চেষ্টা করুন।'
        );
      } else if (err.message === 'OTP session not found') {
        setError(
          'OTP সেশন পাওয়া যায়নি। দয়া করে আবার রেজিস্টার করুন।'
        );
      } else {
        setError(
          'ভেরিফিকেশনে সমস্যা হয়েছে। আবার চেষ্টা করুন।'
        );
      }
    }
  };

  // ==========================================================
  // WebOTP
  // ==========================================================
  // Supported Android browsers can read the OTP SMS after the
  // browser's OTP permission/prompt and automatically fill it.
  // The code is then verified automatically.
  useEffect(() => {
    let abortController;

    const startWebOtp = async () => {
      if (
        !('OTPCredential' in window) ||
        !navigator.credentials?.get
      ) {
        return;
      }

      if (autoVerifyStartedRef.current) return;

      autoVerifyStartedRef.current = true;

      try {
        abortController = new AbortController();

        const credential =
          await navigator.credentials.get({
            otp: { transport: ['sms'] },
            signal: abortController.signal
          });

        const code = String(
          credential?.code || ''
        );

        if (/^\d{6}$/.test(code)) {
          setOtp(code.split(''));

          // Automatic verification after SMS detection.
          await completeVerification(code);
        }
      } catch (err) {
        // WebOTP can fail when unsupported, denied, aborted,
        // or when the SMS format is not compatible.
        console.log(
          'WebOTP unavailable or cancelled:',
          err
        );
      }
    };

    startWebOtp();

    return () => {
      abortController?.abort();
    };
  }, []);

  // ==========================================================
  // Optional native Android bridge
  // ==========================================================
  // A native Android SMS receiver can call:
  //
  // window.receiveNexoraOtp('123456')
  //
  // Then this React page will automatically fill and verify it.
  //
  // This is useful when Nexora is running inside a native
  // Android WebView. The native SMS receiver itself must be
  // implemented on the Android side.
  useEffect(() => {
    window.receiveNexoraOtp = (code) => {
      const cleanCode = String(code || '')
        .replace(/\D/g, '')
        .slice(0, 6);

      if (!/^\d{6}$/.test(cleanCode)) return;

      setOtp(cleanCode.split(''));

      if (!autoVerifyStartedRef.current) {
        autoVerifyStartedRef.current = true;
        completeVerification(cleanCode);
      }
    };

    return () => {
      try {
        delete window.receiveNexoraOtp;
      } catch {
        window.receiveNexoraOtp = undefined;
      }
    };
  }, []);

  const handleChange = (value, index) => {
    const cleanValue = String(value || '')
      .replace(/\D/g, '');

    if (!cleanValue) {
      const newOtp = [...otp];
      newOtp[index] = '';
      setOtp(newOtp);
      return;
    }

    // Supports pasting the complete 6-digit OTP.
    if (cleanValue.length > 1) {
      const digits = cleanValue
        .slice(0, 6)
        .split('');

      const newOtp = ['', '', '', '', '', ''];

      digits.forEach((digit, i) => {
        newOtp[i] = digit;
      });

      setOtp(newOtp);

      const focusIndex = Math.min(
        digits.length,
        5
      );

      inputRefs.current[focusIndex]?.focus();

      if (digits.length === 6) {
        completeVerification(digits.join(''));
      }

      return;
    }

    const newOtp = [...otp];

    newOtp[index] =
      cleanValue.charAt(cleanValue.length - 1);

    setOtp(newOtp);

    if (
      cleanValue &&
      index < 5 &&
      inputRefs.current[index + 1]
    ) {
      inputRefs.current[index + 1].focus();
    }

    const completeCode = newOtp.join('');

    if (completeCode.length === 6) {
      completeVerification(completeCode);
    }
  };

  const handleKeyDown = (e, index) => {
    if (
      e.key === 'Backspace' &&
      !otp[index] &&
      index > 0 &&
      inputRefs.current[index - 1]
    ) {
      inputRefs.current[index - 1].focus();
    }
  };

  const resendOtp = async () => {
    setError('');

    const tempUserData =
      getPendingRegistration();

    if (!tempUserData?.phoneNumber) {
      setError(
        'রেজিস্ট্রেশন সেশন পাওয়া যায়নি। আবার রেজিস্টার করুন।'
      );
      return;
    }

    setIsLoading(true);

    try {
      const formattedPhone = normalizePhone(
        tempUserData.phoneNumber
      );

      const localNumber =
        formattedPhone.replace('+880', '0');

      const message = `Nexora OTP: ${otp}`;

      const functionsClient = getFunctions();
      const sendNexoraOtp = httpsCallable(
        functionsClient,
        'sendNexoraOtp'
      );

      const result = await sendNexoraOtp({
        phoneNumber: formattedPhone,
        host: window.location.host
      });

      const otpResult = result.data || {};

      if (!otpResult.success || !otpResult.otp) {
        throw new Error('OTP function did not return a valid result');
      }

      const code = String(otpResult.otp);

      localStorage.setItem(
        OTP_STORAGE_KEY,
        JSON.stringify({
          code,
          phoneNumber: formattedPhone,
          expiresAt:
            Date.now() + OTP_EXPIRY_MS
        })
      );

      setOtp(['', '', '', '', '', '']);

      inputRefs.current[0]?.focus();

      setIsLoading(false);
    } catch (err) {
      console.error(
        'Resend OTP Error:',
        err
      );

      setIsLoading(false);

      setError(
        'OTP পাঠানো যায়নি। Firebase Function এবং MRAM configuration চেক করুন।'
      );
    }
  };

  return (
    <div style={styles.container}>
      <style>{`
        * {
          box-sizing: border-box;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        }

        .otp-card {
          width: 100%;
          max-width: 420px;
          background: #ffffff;
          border-radius: 28px;
          padding: 32px 24px;
          box-shadow: 0 20px 50px rgba(0, 31, 63, 0.08);
          border: 1px solid #f1f5f9;
          z-index: 10;
        }

        .otp-box {
          width: 46px;
          height: 54px;
          border: 1.5px solid #e2e8f0;
          border-radius: 14px;
          background-color: #f8fafc;
          font-size: 20px;
          font-weight: 800;
          color: #0f172a;
          text-align: center;
          outline: none;
          transition: all 0.2s ease;
        }

        .otp-box:focus {
          border-color: #00c6ff;
          background-color: #ffffff;
          box-shadow: 0 0 0 4px rgba(0, 198, 255, 0.15);
        }

        .btn-verify {
          width: 100%;
          padding: 16px;
          background: linear-gradient(135deg, #00c6ff 0%, #0072ff 50%, #4364f7 100%);
          border: none;
          border-radius: 16px;
          color: #ffffff;
          font-size: 15px;
          font-weight: 700;
          cursor: pointer;
        }

        .btn-resend {
          width: 100%;
          margin-top: 10px;
          padding: 12px;
          background: #f1f5f9;
          border: none;
          border-radius: 14px;
          color: #334155;
          font-size: 13px;
          font-weight: 700;
          cursor: pointer;
        }

        .spinner {
          width: 20px;
          height: 20px;
          border: 2.5px solid rgba(255,255,255,0.3);
          border-top-color: #ffffff;
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
          margin: 0 auto;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>

      <div className="otp-card">
        <div style={styles.headerGroup}>
          <div style={styles.logoBox}>
            <span style={styles.logoText}>
              N<span style={{ color: '#00c6ff' }}>
                x
              </span>
            </span>
          </div>

          <h1 style={styles.title}>
            Enter Real OTP Code
          </h1>

          <p style={styles.subtitle}>
            আপনার মোবাইলে আসা ৬ ডিজিটের SMS কোডটি দিন
          </p>
        </div>

        {error && (
          <div style={styles.errorBox}>
            {error}
          </div>
        )}

        <form
          onSubmit={(e) => {
            e.preventDefault();
            completeVerification(
              otp.join('')
            );
          }}
        >
          <div style={styles.otpContainer}>
            {otp.map((digit, index) => (
              <input
                key={index}
                ref={(el) =>
                  (inputRefs.current[index] = el)
                }
                type="text"
                inputMode="numeric"
                autoComplete={
                  index === 0
                    ? 'one-time-code'
                    : 'off'
                }
                maxLength={6}
                value={digit}
                onChange={(e) =>
                  handleChange(
                    e.target.value,
                    index
                  )
                }
                onKeyDown={(e) =>
                  handleKeyDown(e, index)
                }
                className="otp-box"
                aria-label={`OTP digit ${
                  index + 1
                }`}
              />
            ))}
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className="btn-verify"
          >
            {isLoading ? (
              <div className="spinner" />
            ) : (
              'Verify & Create Account'
            )}
          </button>

          <button
            type="button"
            disabled={isLoading}
            className="btn-resend"
            onClick={resendOtp}
          >
            Resend OTP
          </button>
        </form>
      </div>
    </div>
  );
};


const styles = {
  container: {
    minHeight: '100dvh',
    backgroundColor: '#ffffff',
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'center',
    padding: '32px 20px',
    overflowY: 'auto'
  },

  card: {
    width: '100%',
    maxWidth: '400px',
    background: '#ffffff',
    borderRadius: '28px',
    padding: '32px 24px',
    boxShadow: '0 20px 50px rgba(0, 31, 63, 0.08)',
    border: '1px solid #f1f5f9',
    zIndex: 10
  },

  headerGroup: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    marginBottom: '24px',
    textAlign: 'center'
  },

  logoImage: {
    width: '100px',
    height: '100px',
    objectFit: 'contain',
    display: 'block',
    marginBottom: '10px'
  },

  title: {
    fontSize: '22px',
    fontWeight: '800',
    color: '#0f172a',
    margin: '0 0 4px'
  },

  subtitle: {
    fontSize: '12px',
    color: '#64748b',
    margin: 0
  },

  errorBox: {
    backgroundColor: '#fef2f2',
    border: '1px solid #fecaca',
    color: '#ef4444',
    padding: '10px',
    borderRadius: '12px',
    fontSize: '13px',
    textAlign: 'center',
    marginBottom: '16px'
  },

  otpGroup: {
    marginBottom: '16px'
  },

  label: {
    fontSize: '11px',
    fontWeight: '700',
    color: '#475569',
    display: 'block',
    marginBottom: '6px'
  },

  otpContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: '7px',
    width: '100%'
  },

  otpInput: {
    width: '100%',
    minWidth: 0,
    height: '54px',
    padding: '10px 4px',
    backgroundColor: '#f8fafc',
    border: '1.5px solid #e2e8f0',
    borderRadius: '16px',
    fontSize: '22px',
    fontWeight: '800',
    color: '#0f172a',
    textAlign: 'center',
    outline: 'none',
    transition: 'all 0.2s ease'
  },

  hint: {
    fontSize: '12px',
    color: '#64748b',
    textAlign: 'center',
    margin: '10px 0 0'
  },

  btnAction: {
    width: '100%',
    padding: '16px',
    background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 50%, #15803d 100%)',
    border: 'none',
    borderRadius: '16px',
    color: '#ffffff',
    fontSize: '15px',
    fontWeight: '700',
    cursor: 'pointer',
    boxShadow: '0 10px 25px rgba(22, 163, 74, 0.3)'
  },

  btnSecondary: {
    width: '100%',
    padding: '13px',
    backgroundColor: '#f8fafc',
    border: '1.5px solid #e2e8f0',
    borderRadius: '16px',
    color: '#475569',
    fontSize: '13px',
    fontWeight: '700',
    cursor: 'pointer',
    marginTop: '10px'
  },

  footer: {
    marginTop: '20px',
    paddingTop: '16px',
    borderTop: '1px solid #f1f5f9',
    textAlign: 'center'
  },

  footerText: {
    fontSize: '13px',
    color: '#64748b',
    margin: 0
  },

  spinner: {
    width: '20px',
    height: '20px',
    border: '2.5px solid rgba(255,255,255,0.3)',
    borderTopColor: '#ffffff',
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
    margin: '0 auto'
  }
};

export default OtpVerification;
